import type { RoomSummary } from '@crowdset/contracts';
import { Avatar, Backdrop } from '@crowdset/ui';
import { LockSimple, MusicNotes, Star, Users } from '@phosphor-icons/react';
import { Link } from 'react-router';
import { useFavoriteToggle, useMe } from '../lib/queries';
import { cx, timeAgo } from '../ui/kit';

export function RoomCard({ room, index = 0 }: { room: RoomSummary; index?: number }) {
  const me = useMe();
  const fav = useFavoriteToggle();
  const live = room.participantCount > 0;
  return (
    <article
      className="cs-rise group relative flex flex-col overflow-hidden rounded-[1.25rem] border border-white/[0.07] bg-ink-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-colors hover:border-white/15"
      style={{ ['--i' as string]: index }}
    >
      <div className="relative aspect-[16/7] overflow-hidden">
        <Backdrop
          theme={room.theme}
          className="absolute inset-0 h-full w-full transition-transform duration-700 ease-out group-hover:scale-[1.03]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/20 to-transparent" />
        {room.currentDj && (
          <div className="absolute bottom-1 left-4 flex items-end gap-2">
            <Avatar avatarId={room.currentDj.avatarId} size={38} state="dj" />
          </div>
        )}
        <div className="absolute right-3 top-3 flex items-center gap-1.5">
          {room.visibility === 'private' && (
            <span className="flex items-center gap-1 rounded-full bg-ink-950/70 px-2 py-1 text-[11px] text-fg-muted backdrop-blur">
              <LockSimple size={12} weight="bold" aria-hidden /> Private
            </span>
          )}
          <span
            className={cx(
              'flex items-center gap-1.5 rounded-full bg-ink-950/70 px-2 py-1 font-mono text-[11px] backdrop-blur',
              live ? 'text-fg' : 'text-fg-subtle',
            )}
          >
            {live && <span className="cs-pulse-dot h-1.5 w-1.5 rounded-full bg-ok" aria-hidden />}
            <Users size={12} weight="bold" aria-hidden />
            {room.participantCount}/{room.capacity}
          </span>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4 pt-2">
        <div className="flex items-start gap-2">
          <h3 className="flex-1 text-[15px] font-semibold leading-snug tracking-tight">
            <Link
              to={`/r/${room.slug}`}
              className="rounded outline-none after:absolute after:inset-0 focus-visible:underline"
            >
              {room.name}
            </Link>
          </h3>
          {me && (
            <button
              className={cx(
                'relative z-10 -mr-1 -mt-1 rounded-lg p-1.5 transition-colors hover:bg-white/[0.07]',
                room.isFavorite ? 'text-woot' : 'text-fg-subtle hover:text-fg',
              )}
              aria-pressed={room.isFavorite}
              aria-label={
                room.isFavorite ? `Remove ${room.name} from saved rooms` : `Save ${room.name}`
              }
              onClick={() => fav.mutate({ roomId: room.id, on: !room.isFavorite })}
            >
              <Star size={16} weight={room.isFavorite ? 'fill' : 'regular'} />
            </button>
          )}
        </div>
        {room.genreTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {room.genreTags.map((t) => (
              <span
                key={t}
                className="rounded-md bg-white/[0.05] px-1.5 py-0.5 text-[11px] text-fg-muted"
              >
                {t}
              </span>
            ))}
          </div>
        )}
        <div className="mt-auto flex items-center gap-2 pt-1 text-[13px] text-fg-muted">
          <MusicNotes size={14} className="shrink-0 text-fg-subtle" aria-hidden />
          {room.currentTrack ? (
            <span className="truncate">
              <span className="text-fg">{room.currentTrack.title}</span>
              {room.currentDj && (
                <span className="text-fg-subtle"> · {room.currentDj.displayName}</span>
              )}
            </span>
          ) : (
            <span className="truncate text-fg-subtle">
              {live ? 'Waiting for a DJ' : `Quiet · last active ${timeAgo(room.lastActiveAt)}`}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

export function RoomCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-[1.25rem] border border-white/[0.05] bg-ink-900">
      <div className="cs-skeleton aspect-[16/7]" />
      <div className="flex flex-col gap-2 p-4">
        <div className="cs-skeleton h-4 w-2/3 rounded-md" />
        <div className="cs-skeleton h-3 w-1/3 rounded-md" />
        <div className="cs-skeleton mt-2 h-3 w-5/6 rounded-md" />
      </div>
    </div>
  );
}
